import { useState } from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CircleAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "./profile-form";

const controlClass =
  "h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)";

const REASONS = [
  { value: "Taking a break", label: msg`Taking a break` },
  { value: "Privacy concerns", label: msg`Privacy concerns` },
  { value: "Too many notifications", label: msg`Too many notifications` },
  { value: "Switching to another account", label: msg`Switching to another account` },
  { value: "Other", label: msg`Other` },
];

export function DeactivateAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const [reason, setReason] = useState<string>();
  const [details, setDetails] = useState("");
  const [password, setPassword] = useState("");

  const reset = () => {
    setReason(undefined);
    setDetails("");
    setPassword("");
  };

  const handleDeactivate = () => {
    // No deactivation backend yet (deferred) — presentational confirmation.
    toast.success(t`Account deactivation requested.`);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Deactivate account</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Temporarily disable your account. You won&rsquo;t be able to sign
              in until access is restored.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 rounded-lg bg-(--color-warning-bg) p-3">
            <CircleAlert className="mt-0.5 size-5 shrink-0 text-(--color-warning-text)" />
            <div>
              <p className="text-sm font-medium text-(--color-warning-text)">
                <Trans>Please note</Trans>
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                <Trans>
                  Your profile and settings will be preserved, but you will lose
                  access until your account is reactivated.
                </Trans>
              </p>
            </div>
          </div>

          <Field label={t`Reason`}>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className={controlClass}>
                <SelectValue placeholder={t`Select`} />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {t(r.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="deactivate-details"
              className="text-xs font-medium text-(--color-text-primary)"
            >
              <Trans>Reason (optional)</Trans>
            </label>
            <textarea
              id="deactivate-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={t`Share why you want to deactivate your account…`}
              className="min-h-32 w-full resize-none rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 py-2.5 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus-visible:border-(--color-ring)"
            />
          </div>

          <Field label={t`Confirm password`} htmlFor="deactivate-password">
            <Input
              id="deactivate-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t`Enter password to continue…`}
              className={controlClass}
            />
          </Field>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDeactivate}
            disabled={password.length === 0}
          >
            <Trans>Deactivate account</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
